/**
 * Delivery receipts the server refused as unauthenticated, held until it
 * will take them.
 *
 * The delivery endpoint is authenticated, so the one failure it can never
 * hear about in the moment is the one where auth is what failed: the send
 * 401s, and the beacon reporting that 401 is refused by the same missing
 * cookie. The receipt waits here instead and is filed from the next page
 * that can — the Reconnect reload is built to be exactly that page.
 *
 * sessionStorage, deliberately: it survives the reload in this tab and dies
 * with it, the same lifetime as the masquerade pin already stored there. A
 * receipt must never be filed from another person's session on a shared
 * browser, and the headers it carries are that tab's own.
 *
 * Each receipt names the signed-in person whose attempt it describes, and is
 * filed only from that person's session. A session that expired never runs
 * logout, so the next sign-in on this tab can be someone else, and the server
 * would attribute a receipt to whoever files it.
 *
 * Pure over an injected Storage so it tests without a browser.
 */

export interface DeferredBeacon {
  conversationId: string;
  /** The payload exactly as first sent, navigation type included. */
  payload: Record<string, unknown>;
  /** Tenant pins frozen with the original attempt. */
  headers: Record<string, string>;
  deferredAtMs: number;
  /** Refusals so far, including the first. */
  tries: number;
  /** The signed-in person whose attempt this describes. */
  userId?: string;
}

const STORAGE_KEY = 'leo.deliveryOutbox';
export const OUTBOX_MAX_ENTRIES = 20;
/** A receipt older than this describes a sitting nobody is still reading. */
export const OUTBOX_MAX_AGE_MS = 2 * 60 * 60 * 1000;
/** A jar that keeps dropping the cookie keeps refusing; stop re-queueing. */
export const OUTBOX_MAX_TRIES = 5;

function read(storage: Storage): DeferredBeacon[] {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as DeferredBeacon[]) : [];
  } catch {
    return [];
  }
}

function write(storage: Storage, entries: DeferredBeacon[]): void {
  try {
    if (entries.length) storage.setItem(STORAGE_KEY, JSON.stringify(entries));
    else storage.removeItem(STORAGE_KEY);
  } catch { /* A blocked storage loses the receipt; the server log still has the refusal. */ }
}

function usable(entry: DeferredBeacon, nowMs: number): boolean {
  return Boolean(entry)
    && typeof entry.conversationId === 'string'
    && typeof entry.payload === 'object' && entry.payload !== null
    && typeof entry.headers === 'object' && entry.headers !== null
    && typeof entry.deferredAtMs === 'number'
    && nowMs - entry.deferredAtMs < OUTBOX_MAX_AGE_MS
    && typeof entry.tries === 'number'
    && entry.tries < OUTBOX_MAX_TRIES;
}

/** Queue a refused receipt. Oldest drops first past the cap. */
export function deferBeacon(
  entry: Omit<DeferredBeacon, 'deferredAtMs' | 'tries'> & { deferredAtMs?: number; tries?: number },
  storage: Storage,
  nowMs: number,
): void {
  const next: DeferredBeacon = {
    conversationId: entry.conversationId,
    payload: entry.payload,
    headers: entry.headers,
    deferredAtMs: entry.deferredAtMs ?? nowMs,
    tries: (entry.tries ?? 0) + 1,
    userId: entry.userId,
  };
  if (!usable(next, nowMs)) return;
  const entries = read(storage).filter((e) => usable(e, nowMs));
  entries.push(next);
  write(storage, entries.slice(-OUTBOX_MAX_ENTRIES));
}

/**
 * Every receipt `userId` may still file, removed from the queue. Anyone
 * else's — and any that cannot name its person — are dropped, never filed.
 */
export function takeDeferredBeacons(storage: Storage, nowMs: number, userId?: string): DeferredBeacon[] {
  const entries = read(storage).filter(
    (e) => usable(e, nowMs) && Boolean(userId) && e.userId === userId,
  );
  write(storage, []);
  return entries;
}

/** Drop every held receipt — on sign-out, the next session must not file them as its own. */
export function clearDeferredBeacons(storage: Storage): void {
  write(storage, []);
}
